import { CalendarDay, formatDate, formatMinutes, getProjectColor } from '@/lib/calendar';
import { Clock } from 'lucide-react';

interface WeekViewProps {
  days: CalendarDay[];
  onDayClick: (date: Date) => void;
}

export function WeekView({ days, onDayClick }: WeekViewProps) {
  const timeSlots = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* 曜日ヘッダー */}
      <div className="grid grid-cols-8 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="p-3 border-r border-gray-200"></div>
        {days.map((day) => {
          const dayOfWeek = day.date.getDay();
          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return (
            <div
              key={day.date.toISOString()}
              className={`p-3 text-center border-r border-gray-200 ${
                day.isToday ? 'bg-blue-100' : ''
              }`}
            >
              <div
                className={`text-xs font-medium ${
                  isWeekend
                    ? dayOfWeek === 0
                      ? 'text-red-600'
                      : 'text-blue-600'
                    : 'text-gray-600'
                }`}
              >
                {formatDate(day.date, 'E')}
              </div>
              <div
                className={`text-lg font-bold mt-1 ${
                  day.isToday
                    ? 'bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center mx-auto'
                    : 'text-gray-800'
                }`}
              >
                {day.date.getDate()}
              </div>
              {day.totalMinutes > 0 && (
                <div className="flex items-center justify-center gap-1 text-xs text-blue-600 font-semibold mt-1">
                  <Clock className="w-3 h-3" />
                  <span>{formatMinutes(day.totalMinutes)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* タイムグリッド */}
      <div className="grid grid-cols-8 overflow-y-auto max-h-[600px]">
        {/* 時間列 */}
        <div className="border-r border-gray-200">
          {timeSlots.map((hour) => (
            <div
              key={hour}
              className="h-16 px-2 py-1 text-xs text-gray-500 text-right border-b border-gray-100"
            >
              {hour.toString().padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {/* 各日の列 */}
        {days.map((day) => (
          <button
            key={day.date.toISOString()}
            onClick={() => onDayClick(day.date)}
            className="border-r border-gray-200 hover:bg-blue-50 transition-colors relative"
          >
            {timeSlots.map((hour) => (
              <div
                key={hour}
                className="h-16 border-b border-gray-100 relative"
              >
                {/* タイムレコードをここに配置（簡易版） */}
                {day.records
                  .filter((record) => {
                    const recordHour = record.clockInTime.toDate().getHours();
                    return recordHour === hour;
                  })
                  .map((record, idx) => {
                    if (!record) return null;
                    
                    const projectId = record.projectId || record.project?.id;
                    return (
                      <div
                        key={idx}
                        className={`absolute inset-x-1 top-1 px-2 py-1 rounded text-xs ${getProjectColor(
                          projectId
                        )} bg-opacity-20 border-l-4 ${getProjectColor(
                          projectId
                        )} border-opacity-100 truncate`}
                      >
                        <div className="font-semibold">{record.notes || record.memo || record.project?.name || 'プロジェクト'}</div>
                        <div className="text-gray-600">
                          {formatMinutes(record.totalMinutes || 0)}
                        </div>
                      </div>
                    );
                  })}
              </div>
            ))}
          </button>
        ))}
      </div>
    </div>
  );
}
